<template>
  <div class="card p-6 m-6 rqa_admin_page" dir="rtl">
    <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
      <p class="card-title m-0">פניות העובדות — דבר עם המערכת</p>
      <button type="button" class="button is-light" :class="{ 'is-loading': loading }" @click="refresh">רענון</button>
    </div>

    <div class="rqa_admin_filters mb-4">
      <div class="select is-small">
        <select :value="filterAdminId || ''" aria-label="סינון לפי יועצת" @change="onAdminChange">
          <option value="">כל העובדות</option>
          <option v-for="a in admins" :key="a.id" :value="a.id">{{ optionLabel(a) }}</option>
        </select>
      </div>
      <div class="tabs is-toggle is-small m-0">
        <ul>
          <li v-for="tab in tabs" :key="tab.value" :class="{ 'is-active': filterState === tab.value }">
            <a role="button" :aria-pressed="filterState === tab.value ? 'true' : 'false'" @click="setTab(tab.value)">{{ tab.label }}</a>
          </li>
        </ul>
      </div>
    </div>

    <div class="rqa_admin_body">
      <div class="rqa_admin_list">
        <div v-if="loading && !tickets.length" class="has-text-centered py-6" role="status">
          <p>טוען פניות…</p>
        </div>
        <div v-else-if="error && !tickets.length" class="has-text-centered py-6">
          <p class="rqa_error" role="alert">{{ error }}</p>
          <button type="button" class="button is-small mt-2" @click="reload">נסי שוב</button>
        </div>
        <div v-else-if="isEmpty" class="has-text-centered py-6">
          <p class="rqa_muted">אין פניות עדיין</p>
        </div>
        <template v-else>
          <div class="table-container">
            <table class="table is-fullwidth is-hoverable is-narrow rqa_admin_table">
              <thead>
                <tr>
                  <th>יועצת</th>
                  <th>כותרת</th>
                  <th>סטטוס</th>
                  <th>עדכון אחרון</th>
                  <th>מס' הודעות</th>
                  <th>פנייה #</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="t in tickets"
                  :key="t.root_id"
                  class="rqa_admin_row"
                  :class="{ 'is-selected': selectedRootId === t.root_id }"
                  tabindex="0"
                  @click="open(t.root_id, $event)"
                  @keydown.enter.prevent="open(t.root_id, $event)">
                  <td class="rqa_nowrap">{{ t.admin_name || ('#' + t.admin_id) }}</td>
                  <td class="rqa_title_cell">
                    {{ titleOf(t) }}
                    <span v-if="t.closed_at" class="tag is-light is-small">סגורה</span>
                    <template v-for="jid in (t.jira_task_ids || [])">
                      <span :key="jid + ':task'" class="tag is-small rqa_task_tag">משימה</span>
                      <span v-if="jiraStatusOf(jid)" :key="jid + ':status'" class="tag is-small rqa_task_status" :class="jiraStatusOf(jid).cls" :title="'סטטוס המשימה ' + jiraShort(jid)">{{ jiraStatusOf(jid).label }}</span>
                    </template>
                  </td>
                  <td><recruiter-qa-status-pill :status="t.latest_status" supervisor /></td>
                  <td class="rqa_nowrap" :title="absTime(t.last_activity_at)">{{ relTime(t.last_activity_at) }}</td>
                  <td>{{ messageCount(t) }}</td>
                  <td>{{ t.root_id }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="error" class="rqa_error" role="alert">{{ error }} <a href="#" @click.prevent="retryMore">נסי שוב</a></p>
          <div v-if="hasMore" class="has-text-centered mt-3">
            <button type="button" class="button is-small" :class="{ 'is-loading': loadingMore }" @click="loadMore">טען עוד</button>
          </div>
        </template>
      </div>

      <div v-if="thread.rootId" class="rqa_admin_panel">
        <recruiter-qa-admin-thread
          :key="thread.rootId"
          :thread="thread"
          :can-write="canWrite"
          @close="closeThread"
          @retry="open(thread.rootId)" />
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import RecruiterQaStatusPill from '@/components/recruiterQa/RecruiterQaStatusPill'
import RecruiterQaAdminThread from '@/components/recruiterQa/RecruiterQaAdminThread'
import { ticketTitle, relativeTime, dateTime, adminOptionLabel, jiraShortId } from '@/utils/recruiterQaFormat'
import { jiraStatusLabel, jiraStatusTagClass, JIRA_STATUS_UNAVAILABLE } from '@/utils/recruiterQaJira'

// Supervisor view of every recruiter's tickets. Access is enforced server-side
// (403); the menu entry is gated on GET /config `supervisor` (server-decided);
// write controls on GET /config `supervisor_writes`.
// No polling: "רענון" reloads on demand. Never acks recruiter unread.
// Deep link /recruiter-qa/all?recruiter_question=<root_id> opens that thread
// regardless of the list filters.
export default {
  name: 'RecruiterQaAdmin',
  components: { RecruiterQaStatusPill, RecruiterQaAdminThread },
  data () {
    return {
      tabs: [
        { value: 'open', label: 'פתוחות' },
        { value: 'closed', label: 'סגורות' },
        { value: 'all', label: 'הכל' }
      ]
    }
  },
  computed: {
    ...mapState('recruiterQaAdmin', ['tickets', 'admins', 'filterState', 'filterAdminId', 'loading', 'loadingMore', 'error', 'thread', 'jiraStatuses']),
    ...mapGetters('recruiterQaAdmin', ['hasMore', 'isEmpty', 'canWrite']),
    selectedRootId () { return this.thread.rootId },
    deepLinkId () {
      const q = this.$route && this.$route.query
      const id = parseInt(q && q.recruiter_question, 10)
      return id > 0 ? id : null
    }
  },
  watch: {
    deepLinkId (id) { if (id) this.open(id) }
  },
  created () {
    this.lastRow = null // non-reactive: the row element to refocus on close
    this.$store.dispatch('recruiterQaAdmin/loadTickets')
    if (this.deepLinkId) this.open(this.deepLinkId)
  },
  beforeDestroy () {
    this.$store.dispatch('recruiterQaAdmin/closeThread')
  },
  methods: {
    titleOf (t) { return ticketTitle(t.title) || 'פנייה' },
    relTime (iso) { return relativeTime(iso) },
    absTime (iso) { return dateTime(iso) },
    optionLabel (a) { return adminOptionLabel(a) },
    messageCount (t) { return typeof t.message_count === 'number' ? t.message_count : '—' },
    jiraShort (id) { return jiraShortId(id) },
    // null while the status was not asked yet (no tag); the "unavailable" tag
    // only after the server answered null for this id.
    jiraStatusOf (jid) {
      const key = String(jid)
      if (!Object.prototype.hasOwnProperty.call(this.jiraStatuses, key)) return null
      const jira = this.jiraStatuses[key]
      if (!jira) return { label: JIRA_STATUS_UNAVAILABLE, cls: 'is-light' }
      return { label: jiraStatusLabel(jira) || JIRA_STATUS_UNAVAILABLE, cls: jiraStatusTagClass(jira) }
    },
    setTab (value) {
      if (value === this.filterState) return
      this.$store.dispatch('recruiterQaAdmin/setFilters', { filterState: value })
    },
    onAdminChange (event) {
      this.$store.dispatch('recruiterQaAdmin/setFilters', { filterAdminId: event.target.value || null })
    },
    reload () { this.$store.dispatch('recruiterQaAdmin/loadTickets') },
    retryMore () {
      if (this.hasMore) return this.loadMore()
      return this.reload()
    },
    loadMore () { this.$store.dispatch('recruiterQaAdmin/loadMore') },
    refresh () { this.$store.dispatch('recruiterQaAdmin/refresh') },
    open (rootId, event) {
      if (event && event.currentTarget) this.lastRow = event.currentTarget
      this.$store.dispatch('recruiterQaAdmin/openThread', rootId)
    },
    // Close the panel and hand focus back to the row that opened it.
    closeThread () {
      const row = this.lastRow
      this.lastRow = null
      this.$store.dispatch('recruiterQaAdmin/closeThread')
      // Drop the deep-link param so the same link can be followed again.
      if (this.deepLinkId && this.$router && typeof this.$router.replace === 'function') {
        const query = { ...this.$route.query }
        delete query.recruiter_question
        const nav = this.$router.replace({ query })
        if (nav && typeof nav.catch === 'function') nav.catch(() => {})
      }
      this.$nextTick(() => { if (row && row.isConnected !== false && typeof row.focus === 'function') row.focus() })
    }
  }
}
</script>
<style scoped>
.rqa_admin_filters { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
.rqa_admin_body { display: flex; align-items: flex-start; gap: 16px; }
.rqa_admin_list { flex: 1; min-width: 0; }
.rqa_admin_panel {
  width: 460px;
  flex-shrink: 0;
  max-height: calc(100vh - 160px);
  position: sticky;
  top: 16px;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  padding: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.rqa_admin_table th { text-align: right; white-space: nowrap; }
.rqa_admin_row { cursor: pointer; }
.rqa_title_cell { word-break: break-word; }
.rqa_task_tag { background-color: #2457c5; color: #fff; font-weight: 700; }
.rqa_admin_row.is-selected .rqa_task_tag { background-color: #fff; color: #2457c5; }
.rqa_task_status { margin-inline-start: 2px; font-weight: 600; }
.rqa_nowrap { white-space: nowrap; }
.rqa_muted { color: #777; }
.rqa_error { color: #a12620; }
@media (max-width: 900px) {
  .rqa_admin_panel {
    position: fixed;
    inset: 0;
    width: auto;
    max-height: none;
    border-radius: 0;
    z-index: 40;
  }
}
</style>
